import { addDays, format } from "date-fns";
import type { ExpertSessionMode } from "@/services/api";
import { parseDateInput } from "@/utils/formatDisplayDate";

/**
 * Expert Program length, as one select value.
 *
 * The API stores the length as duration_days OR duration_weeks (never both),
 * so the select works on one encoded string:
 *
 *   "d1" "d2" "d4"  -> 1, 2 or 4 days
 *   "w1" ... "w12"  -> weeks ("1 week" is duration_weeks 1)
 *
 * The session FORMAT (single vs multi-session) is a separate choice made on
 * the Sessions step -- a 1-day program can be one session or several in the
 * same day. Single session is only offered for 1-day programs; every other
 * length is multi-session (the server enforces this too).
 */
export const EXPERT_DURATION_OPTIONS = [
  { label: "1 Day", value: "d1" },
  { label: "2 Days", value: "d2" },
  { label: "4 Days", value: "d4" },
  { label: "1 Week", value: "w1" },
  ...Array.from({ length: 11 }, (_, i) => i + 2).map((w) => ({ label: `${w} Weeks`, value: `w${w}` })),
];

export const DEFAULT_EXPERT_DURATION = "w12";
export const ONE_DAY_DURATION = "d1";

export interface ExpertDurationFields {
  session_mode?: ExpertSessionMode | null;
  duration_days?: number | null;
  duration_weeks?: number | null;
}

/** API fields -> select value. Falls back to the 12-week default when unset. */
export const durationFieldsToValue = (fields: ExpertDurationFields): string => {
  if (fields.duration_days) return `d${fields.duration_days}`;
  if (fields.duration_weeks) return `w${fields.duration_weeks}`;
  // A single session is a 1-day program by definition.
  if (fields.session_mode === "SINGLE") return ONE_DAY_DURATION;
  return DEFAULT_EXPERT_DURATION;
};

/**
 * Select value -> the PATCH/POST duration field. Sends exactly one unit; the
 * server clears the other, and switches a single-session program back to multi
 * when it is no longer 1 day long.
 */
export const durationValueToFields = (value: string): { duration_days?: number; duration_weeks?: number } => {
  const amount = Number(value.slice(1));
  if (value.startsWith("d") && Number.isFinite(amount)) return { duration_days: amount };
  if (value.startsWith("w") && Number.isFinite(amount)) return { duration_weeks: amount };
  return { duration_weeks: 12 };
};

/** Only a 1-day program may run as a single session. */
export const canBeSingleSession = (fields: ExpertDurationFields): boolean => fields.duration_days === 1;

export const isSingleSession = (fields: ExpertDurationFields): boolean => fields.session_mode === "SINGLE";

/** Human label: "1 day · single session", "4 days", "1 week", "6 weeks". */
export const formatProgramDuration = (fields: ExpertDurationFields): string | null => {
  const days = fields.duration_days ?? (fields.session_mode === "SINGLE" ? 1 : null);
  const base = days
    ? `${days} day${days === 1 ? "" : "s"}`
    : fields.duration_weeks
      ? `${fields.duration_weeks} week${fields.duration_weeks === 1 ? "" : "s"}`
      : null;
  if (!base) return null;
  return fields.session_mode === "SINGLE" ? `${base} · single session` : base;
};

/** Short form for compact chips: "1d", "2d", "6w". */
export const formatProgramDurationShort = (fields: ExpertDurationFields): string | null => {
  const days = fields.duration_days ?? (fields.session_mode === "SINGLE" ? 1 : null);
  if (days) return `${days}d`;
  if (fields.duration_weeks) return `${fields.duration_weeks}w`;
  return null;
};

/** Total length in days, for date-picker bounds. Single session -> 1 day. */
export const programLengthInDays = (fields: ExpertDurationFields): number | null => {
  if (fields.duration_days) return fields.duration_days;
  if (fields.session_mode === "SINGLE") return 1;
  if (fields.duration_weeks) return fields.duration_weeks * 7;
  return null;
};

export const MIN_PROGRAM_SLOTS = 1;
export const MAX_PROGRAM_SLOTS = 100;
export const DEFAULT_PROGRAM_SLOTS = 10;

/**
 * When the program is over, as an instant.
 *
 * Prefers `wrapup_ends_at` -- the end time of the last group session -- so
 * wrap-up opens the moment that session ends, including for a single-session or
 * one-day program whose only session is today. `ends_at` is a date with no
 * time, so it parses to a point inside that calendar day, not the end of the
 * last session: using it alone could open wrap-up before the last session even
 * began. It is kept only as a fallback for payloads that don't carry
 * wrapup_ends_at.
 */
export const getProgramEndTime = (detail: {
  wrapup_ends_at?: string | null;
  ends_at?: string | null;
}): Date | null => parseDateInput(detail.wrapup_ends_at ?? detail.ends_at ?? null);

export const hasProgramEnded = (
  detail: { wrapup_ends_at?: string | null; ends_at?: string | null } | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!detail) return false;
  const end = getProgramEndTime(detail);
  return !!end && now > end;
};

/** The public (camelCase) program payloads -> ExpertDurationFields. */
export const publicDurationFields = (p: {
  sessionMode?: ExpertSessionMode | null;
  durationDays?: number | null;
  durationWeeks?: number | null;
}): ExpertDurationFields => ({
  session_mode: p.sessionMode ?? null,
  duration_days: p.durationDays ?? null,
  duration_weeks: p.durationWeeks ?? null,
});

/**
 * Day progress for a short program (single session, or 1/2/4 days). These keep
 * the weekly journey shape -- one week on the timeline -- but count progress in
 * days, since "Week 1 of 1" says nothing about a 2-day program.
 *
 * Returns null for a week-based program. currentDay is 0 before kick-off and
 * is clamped to totalDays afterwards.
 */
export const getShortProgramDayProgress = (
  fields: ExpertDurationFields,
  kickoffDate: string | Date | null | undefined,
  now: Date = new Date(),
): { currentDay: number; totalDays: number } | null => {
  const totalDays = fields.duration_days ?? (fields.session_mode === "SINGLE" ? 1 : null);
  if (!totalDays) return null;
  const kickoff = parseDateInput(kickoffDate);
  if (!kickoff) return { currentDay: 0, totalDays };
  const start = new Date(kickoff);
  start.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const elapsed = Math.floor((today.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
  if (elapsed < 0) return { currentDay: 0, totalDays };
  return { currentDay: Math.min(totalDays, elapsed + 1), totalDays };
};

/** "Day 1 of 2" -- or just "Day 1" for a one-day / single-session program. */
export const formatDayProgress = (currentDay: number, totalDays: number): string =>
  totalDays === 1 ? `Day ${currentDay}` : `Day ${currentDay} of ${totalDays}`;

export interface DateBounds {
  min: string;
  max: string;
}

/**
 * Where sessions may be scheduled, measured from the Kick-off's date (Day 1 /
 * Week 1) and the program's length. One definition for the wizard and the
 * program page so the two can never disagree.
 *
 *   N days   program  = Kick-off day .. day N
 *            wrap-up  = the same window (any of the program's days)
 *   N weeks  program  = Kick-off day .. last day of week N
 *            wrap-up  = the final week only (days 7(N-1)+1 .. 7N), matching the
 *                       backend's week bucketing (days since kick-off // 7)
 *   single   no separate wrap-up or additional sessions -> null
 *
 * Additional sessions go BETWEEN the Kick-off and the Wrap-up: when a Wrap-up
 * is scheduled (`wrapupStart`), the program window is capped at its date, so a
 * new session can never land after it and quietly become the last session --
 * which would move when wrap-up opens for achievers.
 *
 * Dates are "yyyy-MM-dd" in the viewer's local calendar, like the date input.
 */
export const computeSessionDateBounds = (
  kickoffStart: string | null | undefined,
  fields: ExpertDurationFields,
  wrapupStart?: string | null,
): { wrapup: DateBounds | null; program: DateBounds | null } => {
  const kickoff = parseDateInput(kickoffStart);
  if (!kickoff || fields.session_mode === "SINGLE") return { wrapup: null, program: null };
  const day = (offset: number) => format(addDays(kickoff, offset), "yyyy-MM-dd");
  const wrapup = parseDateInput(wrapupStart);
  const wrapupDay = wrapup ? format(wrapup, "yyyy-MM-dd") : null;
  const capAtWrapup = (window: DateBounds): DateBounds =>
    wrapupDay && wrapupDay < window.max ? { min: window.min, max: wrapupDay } : window;
  if (fields.duration_days) {
    const window = { min: day(0), max: day(fields.duration_days - 1) };
    return { wrapup: window, program: capAtWrapup(window) };
  }
  if (fields.duration_weeks) {
    const lastDay = fields.duration_weeks * 7 - 1;
    return {
      wrapup: { min: day((fields.duration_weeks - 1) * 7), max: day(lastDay) },
      program: capAtWrapup({ min: day(0), max: day(lastDay) }),
    };
  }
  return { wrapup: null, program: null };
};

/**
 * Where goal / task dates may fall: the program's stored start and end dates.
 * These are exactly what the weekly journey places items between (anything
 * outside is dropped), so the bounds match it rather than re-deriving them.
 * Null until the program has dates (no Kick-off scheduled yet).
 */
export const computeDueDateBounds = (detail: {
  starts_at?: string | null;
  ends_at?: string | null;
} | null | undefined): DateBounds | null =>
  detail?.starts_at && detail?.ends_at
    ? { min: detail.starts_at.slice(0, 10), max: detail.ends_at.slice(0, 10) }
    : null;
